import { TableColumn, types, utils, Badge } from "@vilocnv/allsetra-core";
import { Tooltip } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import LabelIcon from "@mui/icons-material/Label";
import { MileageIcon } from "assets/icons";
import { KeyStatusBadge } from "domain/keys/components/KeyStatusBadge";
import { KeyStatus } from "domain/keys/entities/dtos/key-history.dto";

export const ACCOUNT_KEYS_TABLE_COLUMNS = (
  translator: any,
  data?: types.IKey[],
  selectedStatusFilter?: KeyStatus | "all"
): TableColumn<types.IKey>[] => {
  const showTypeColumn =
    selectedStatusFilter === KeyStatus.Active || selectedStatusFilter === "all";

  const typeColumn: TableColumn<types.IKey> = {
    name: "Type",
    cell: (
      row: types.IKey & {
        keyStatus?: KeyStatus;
        accountUsers?: Array<{ firstName?: string; lastName?: string }>;
      }
    ) => {
      if (row?.keyStatus !== KeyStatus.Active) return null;

      const hasAssignedUsers = row?.accountUsers && row.accountUsers.length > 0;
      const hasLabel = Boolean(row?.label);

      if (hasAssignedUsers) {
        return (
          <Tooltip
            title={translator("keyActivationType.byDriver")}
            arrow
            placement="top"
          >
            <PersonIcon
              sx={{ color: "#c1c1c1", fontSize: 18, cursor: "default" }}
            />
          </Tooltip>
        );
      }

      if (hasLabel) {
        return (
          <Tooltip
            title={translator("keyActivationType.byLabel")}
            arrow
            placement="top"
          >
            <LabelIcon
              sx={{ color: "#c1c1c1", fontSize: 18, cursor: "default" }}
            />
          </Tooltip>
        );
      }

      return null;
    },
    width: "56px",
    compact: true
  };

  return [
    {
      name: "№",
      selector: (row: types.IKey) => {
        const index =
          data?.findIndex((item) => item.uniqueId === row.uniqueId) ?? -1;
        return index >= 0 ? index + 1 : "";
      },
      sortable: false,
      width: "60px"
    },
    ...(showTypeColumn ? [typeColumn] : []),
    {
      name: translator("tableHeading.status", {
        ns: "tableHeadingsTranslation"
      }),
      cell: (row: types.IKey & { keyStatus?: KeyStatus }) => (
        <KeyStatusBadge keyStatus={row?.keyStatus} translator={translator} />
      ),
      sortable: true,
      sortField: "keyStatus",
      width: "100px"
    },
    {
      name: translator("tableHeading.label", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: types.IKey) => row.label || "",
      sortable: true,
      sortField: "label"
    },
    {
      name: translator("tableHeading.keyID", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: types.IKey) => row?.keyId || "",
      sortable: true,
      sortField: "keyId"
    },
    {
      name: translator("tableHeading.assignedTo", {
        ns: "tableHeadingsTranslation"
      }),
      cell: (
        row: types.IKey & {
          accountUsers?: Array<{ firstName?: string; lastName?: string }>;
        }
      ) => {
        if (!row?.accountUsers || row.accountUsers.length === 0) return "-";

        const userNames = row.accountUsers
          .map((user) =>
            [user.firstName, user.lastName].filter(Boolean).join(" ")
          )
          .filter(Boolean)
          .join(", ");

        const displayName = userNames || "-";

        return (
          <Tooltip title={displayName} arrow placement="top">
            <div
              style={{
                maxWidth: "200px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: "default",
                fontSize: "14px"
              }}
            >
              {displayName}
            </div>
          </Tooltip>
        );
      },
      sortable: false,
      maxWidth: "200px"
    },
    {
      name: translator("tableHeading.activationTimeStamp", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: types.IKey) =>
        row.activationDate
          ? utils.formatDate({ date: row?.activationDate })
          : "N/A",
      sortable: true,
      sortField: "activationDate"
    },
    {
      name: translator("tableHeading.deactivationTimeStamp", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: types.IKey) =>
        row.deactivationDate
          ? utils.formatDate({ date: row?.deactivationDate })
          : "N/A",
      sortable: true,
      sortField: "deactivationDate"
    },
    {
      name: translator("tableHeading.privateMileage", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: types.IKey & { yearPrivateMileage: any }) =>
        row?.yearPrivateMileage !== undefined &&
        row?.yearPrivateMileage !== null ? (
          <Badge
            colorScheme={"info"}
            icon={<MileageIcon />}
            textVariant={"small"}
          >
            {row?.yearPrivateMileage?.toFixed(1)} km
          </Badge>
        ) : (
          "N/A"
        ),
      sortable: true,
      sortField: "yearPrivateMileage"
    }
  ];
};
